/**
 * NgwEditorMailRegion — `IRegion` 의 NgwEditor 구현 (PR-N4).
 *
 * TinyMCERegion 의 §9 매핑표를 그대로 따른다 — 4 region 의 kind 별 지원/미지원 메서드가
 * 호출자 (EmailForm / SignatureEdit) 패턴과 일치하도록.
 *
 * | 메서드      | nBody | signature | quotedHeader | quotedBody |
 * | ----------- | :---: | :-------: | :----------: | :--------: |
 * | set         |  ✓    |    ✓      |      ✓       |     ✓      |
 * | get         |  ✓    |    ✓      |      -       |     -      |
 * | getElement  |  ✓    |    ✓      |      -       |     -      |
 * | has         |  -    |    ✓      |      -       |     ✓      |
 * | remove      |  -    |    ✓      |      -       |     ✓      |
 * | isChanged   |  ✓    |    -      |      -       |     ✓      |
 * | setEditable |  ✓    |    -      |      -       |     ✓      |
 * | isEditable  |  ✓    |    -      |      -       |     -      |
 *
 * ### NgwEditor 의 구현 표면
 *
 * - **set**: EditorHandle.findNodes(kind) 로 기존 노드 위치 탐색.
 *   - 노드 존재 시: `tr.replaceWith(pos, pos + nodeSize, ...)` 로 region 노드 교체.
 *   - 노드 부재 시: doc 끝에 새 region 노드 append.
 *   - nBody 의 `isInsert` 옵션 — 기존 nbody 노드 안에 단순 append.
 *   - nBody 의 `needRestore` 옵션 — 어댑터 lastSnapshot 에서 복원.
 *   - `isInnerHTML` 옵션 — HTML 단편을 region 노드의 content 로 만 사용 (wrapper 제외).
 * - **get**: `DOMSerializer.fromSchema(schema).serializeNode(node)` → HTML.
 * - **getElement**: mountElement 안에서 region 클래스 셀렉터 querySelector.
 * - **has / remove**: findNodes(kind) 활용.
 * - **isChanged**: 어댑터 멤버 `lastSnapshot` 과 현재 get() 비교.
 * - **setEditable / isEditable**: region 노드의 contentEditable attribute 토글.
 *
 * lifecycle.init 전 호출 시 모든 메서드는 silent (TinyMCERegion 와 동일 — handle null 가드).
 */

import {
  DOMParser as PMDOMParser,
  DOMSerializer,
  type EditorView,
  type Node as ProseMirrorNode,
  type Schema,
} from '@ngweditor/react'
import type { IRegion } from '../IRegion'
import type { SetRegionOptions } from '../types'
import type { NgwEditorAdapter } from './NgwEditorAdapter'
import { sanitizeImgDimensions, stripEmptyBlockTrailingBr } from './NgwEditorContent'
import { normalizeHrInlineStyle } from './normalizeHrInlineStyle'
import { REGION_CLASSES, unwrapAutoNbody } from './extensions/mailRegions'

export type RegionKind =
  | 'nBody'
  | 'blankSign'
  | 'signature'
  | 'blankMsg'
  | 'quotedHeader'
  | 'quotedBody'

/** RegionKind → ProseMirror node name (mailRegionsExtension 의 nodes key) */
const NODE_NAME: Record<
  RegionKind,
  'nbody' | 'blank_sign' | 'signature' | 'blank_msg' | 'quoted_header' | 'quoted_body'
> = {
  nBody: 'nbody',
  blankSign: 'blank_sign',
  signature: 'signature',
  blankMsg: 'blank_msg',
  quotedHeader: 'quoted_header',
  quotedBody: 'quoted_body',
}

/**
 * 6-region 의 canonical doc 순서 (docs/editor/mail-body-structure.md §1).
 *
 * NgwEditorMailRegion.set 의 append 분기가 본 순서를 기준으로 새 노드의 insert pos 를
 * 결정한다. EmailForm 답장 흐름의 호출 순서가 어떤 순열이어도 doc 노드 순서가 canonical 로
 * 수렴한다.
 */
const REGION_NODE_ORDER = [
  'nbody',
  'blank_sign',
  'signature',
  'blank_msg',
  'quoted_header',
  'quoted_body',
] as const

/**
 * RegionKind → DOM 클래스 셀렉터. nbody/signature/quoted 는 REGION_CLASSES 의 첫 토큰을
 * '.' prefix 로 사용. blank 는 두 토큰('blank sign')이므로 `split(' ').join('.')` 로 AND 셀렉터
 * (`.blank.sign`) 를 만든다.
 */
const DOM_SELECTOR: Record<RegionKind, string> = {
  nBody: '.' + REGION_CLASSES.nbody.split(' ')[0],
  blankSign: '.' + REGION_CLASSES.blankSign.split(' ').join('.'),
  signature: '.' + REGION_CLASSES.signature.split(' ')[0],
  blankMsg: '.' + REGION_CLASSES.blankMsg.split(' ').join('.'),
  quotedHeader: '.' + REGION_CLASSES.quotedHeader.split(' ')[0],
  quotedBody: '.' + REGION_CLASSES.quotedBody.split(' ')[0],
}

/**
 * Schema 의 DOMSerializer 인스턴스. ngwEditor 0.2.11 부터 정식 export 사용.
 * PM 내부에서 schema.cached.domSerializer 캐시되므로 매 호출이 O(1).
 */
function getDOMSerializer(_view: EditorView, schema: Schema): DOMSerializer {
  return DOMSerializer.fromSchema(schema)
}

/**
 * HTML 단편 → 브라우저 DOM container. 표준 DOMParser 가 script/iframe 무력화.
 *
 * D-2 (ngwEditor-migration-0.2.12): region.set 경로가 코어 setHTML 의 빈 블록 `<br>` 제거 후처리를
 * 우회하던 회귀 차단. p/h1~h6/li/blockquote 의 단독 `<br>` 자식을 제거하여 get/set 라운드트립
 * 멱등성 회복. 코어 setHTML 의 동일 로직(`EditorCore.ts:1604-1609`)과 정합.
 * QA Q2-2 의 "어댑터 wrapper 가 setHTML 정규화 후처리를 우회하면 누적" 가드.
 *
 * @internal 단위 테스트에서 import 하여 정규화 동작 검증.
 */
export function htmlToContainer(html: string): HTMLDivElement {
  const sanitized = stripEmptyBlockTrailingBr(sanitizeImgDimensions(normalizeHrInlineStyle(html)))
  const fragment = `<div id="ngw-region-container">${sanitized || '<p></p>'}</div>`
  const browserDoc = new DOMParser().parseFromString(fragment, 'text/html')
  const container = browserDoc.getElementById('ngw-region-container') as HTMLDivElement | null
  if (container == null) return document.createElement('div')
  return container
}

export class NgwEditorMailRegion implements IRegion {
  private readonly adapter: NgwEditorAdapter
  readonly kind: RegionKind

  /**
   * 마지막 reset 시점의 HTML 스냅샷.
   *
   * B (ngwEditor-migration-0.2.12) 이후 역할: needRestore 분기의 HTML 복원 경로 전용
   * (Node baseline 으로는 외부 HTML 복원 불가). isChanged 비교는 더 이상 본 필드를 사용하지
   * 않음 ([initialRegionNode] 참조). P-B 옵션 (a) 채택 — 병행 유지.
   *
   * `null` 은 reset 미호출 — 변경 없음으로 간주 (resetChangedState 호출 책임).
   */
  private lastSnapshot: string | null = null

  /**
   * B (ngwEditor-migration-0.2.12): 마지막 reset 시점의 region PM Node baseline.
   * `isChanged()` 가 `!current.eq(initialRegionNode)` 로 구조적 비교 (QA Q1-3 옵션 A).
   * HTML 정규화 (td>p unwrap, data-colwidth 변환 등) 와 무관한 false positive 차단.
   *
   * nbody region 이 doc 에 부재한 케이스 (insertMailLayout 미경유 / paragraph 만 있는 단순 본문)
   * 는 doc 전체를 baseline 으로 보관 (`current.doc.eq(initialDoc)` 폴백) — 기존 string 비교의
   * doc 전체 getHTML 폴백과 parity 유지.
   *
   * `null` 은 reset 미호출 또는 quotedHeader (snapshot 미보관) — isChanged 가 false 반환.
   */
  private initialRegionNode: ProseMirrorNode | null = null

  /** contentEditable 상태 (DOM 직접 토글 + 본 멤버에 보존). */
  private editable = true

  /**
   * dev 환경 lazy assert 가 본 인스턴스에서 이미 발화/통과되었는지 플래그. 인스턴스마다 1회.
   * `set()` 의 첫 성공 호출 직후 DOM 마운트가 진행된 시점에 DOM_SELECTOR 와 실제 mount DOM 의
   * 일관성을 점검 (REGION_CLASSES / NodeView wrapper / toDOM 클래스 사이의 mismatch 회귀 감지).
   */
  private domSelectorAsserted = false

  constructor(adapter: NgwEditorAdapter, kind: RegionKind) {
    this.adapter = adapter
    this.kind = kind
  }

  /** state.resetChangedState 가 호출. @internal */
  resetSnapshot(): void {
    // quotedHeader 는 isChanged 미지원 + needRestore 미사용이라 양 baseline 모두 null.
    if (this.kind === 'quotedHeader') {
      this.lastSnapshot = null
      this.initialRegionNode = null
      return
    }
    // B (ngwEditor-migration-0.2.12): Node baseline + HTML snapshot 병행 캡쳐 (P-B 옵션 (a)).
    // 비교(isChanged)는 Node.eq, 복원(needRestore)은 HTML.
    try {
      this.lastSnapshot = this.serialize()
    } catch {
      this.lastSnapshot = null
    }
    try {
      this.initialRegionNode = this.captureRegionNode()
    } catch {
      this.initialRegionNode = null
    }
  }

  /**
   * 현재 시점의 region PM Node 인스턴스 캡쳐. region 부재 시 nbody 만 doc 전체 폴백 (string
   * 비교 시점의 `serialize()` getHTML 폴백과 parity).
   *
   * @internal
   */
  private captureRegionNode(): ProseMirrorNode | null {
    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return null
    const nodes = handle.findNodes(NODE_NAME[this.kind])
    if (nodes.length > 0) return nodes[0].node
    // nbody region 부재 시 doc 전체. signature / quoted 는 region 부재 = 부재 (null).
    if (this.kind === 'nBody') return core.view.state.doc
    return null
  }

  set(html: string, opts?: SetRegionOptions): void {
    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return

    const { view } = core
    const { state } = view
    const { schema } = state
    const nodeType = schema.nodes[NODE_NAME[this.kind]]
    if (nodeType == null) return

    const parser = PMDOMParser.fromSchema(schema)
    const container = htmlToContainer(html)
    const parsed = parser.parse(container)
    // A-1.fix (ngwEditor-migration-0.2.12): PMDOMParser.parse 가 doc.content 시퀀스 강제로
    // 외부 paragraph 를 nbody 안에 자동 wrap 하는 부작용 차단. 자세한 메커니즘은
    // extensions/mailRegions.ts 의 unwrapAutoNbody 주석 참조.
    const parsedContent = unwrapAutoNbody(parsed.content)

    const nodes = handle.findNodes(NODE_NAME[this.kind])

    if (this.kind === 'nBody' && opts?.needRestore === true && this.lastSnapshot != null) {
      const restoreContainer = htmlToContainer(this.lastSnapshot)
      const restored = parser.parse(restoreContainer)
      const restoredContent = unwrapAutoNbody(restored.content)
      const newNode = nodeType.create({}, restoredContent)
      this.replaceOrAppend(newNode, nodes)
      return
    }

    if (this.kind === 'nBody' && opts?.isInsert === true && nodes.length > 0) {
      // 기존 nbody content 뒤에 새 단편 append.
      const existing = nodes[0]
      const merged = existing.node.content.append(parsedContent)
      const newNode = nodeType.create(existing.attrs, merged)
      const tr = state.tr.replaceWith(existing.pos, existing.pos + existing.node.nodeSize, newNode)
      view.dispatch(tr)
      return
    }

    // quoted_header 와 quoted_body 의 collapsed 는 외곽 토글 버튼이 한 transaction 으로 동기 토글
    // 하므로 초기 set 시점에도 둘이 같은 값(기본: 접힘)을 가져야 quoted_header_editor wrapper 에
    // 도 fold 클래스가 같이 부여된다. quoted_header.attrs.collapsed default 는 false 라 명시
    // 없이 set 하면 quoted_body 는 fold, quoted_header 는 not fold 로 처음부터 불일치.
    const attrs: Record<string, unknown> =
      this.kind === 'quotedBody' || this.kind === 'quotedHeader' ? { collapsed: true } : {}
    const newNode = nodeType.create(attrs, parsedContent)

    // PM doc 마운트 직후 nBody.set 가 처음 호출되는 경우 doc 에는 default paragraph 만 있다.
    // `replaceOrAppend` 의 canonical insert 는 doc 의 끝 (= default paragraph 뒤) 에 nbody 를
    // 추가하므로 getHTML 결과의 첫 자식이 빈 `<p></p>` 로 남는 회귀가 있다 (mail-sample 의
    // initMailBody 는 doc 전체 교체로 이 paragraph 를 제거). doc 가 "default paragraph 만 있는
    // 빈 상태" 인 경우 doc.content 전체를 새 nbody 로 교체해 mail-sample 과 동등하게 동작시킴.
    if (this.kind === 'nBody' && nodes.length === 0) {
      const firstChild = state.doc.content.firstChild
      const onlyDefaultParagraph =
        state.doc.content.childCount === 1 &&
        firstChild?.type.name === 'paragraph' &&
        firstChild.content.size === 0
      if (onlyDefaultParagraph) {
        const tr = state.tr.replaceWith(0, state.doc.content.size, newNode)
        view.dispatch(tr)
        return
      }
    }

    this.replaceOrAppend(newNode, nodes)

    // 6-region: signature / quoted 를 개별 set 하는 호출부(EmailForm 등, insertMailLayout 미경유)
    // 에서도 companion blank 영역이 보장되도록 set 직후 자동 생성 (이미 있으면 no-op).
    this.ensureCompanionBlank()

    // PR-N10 (0.2.11) — dev 환경 lazy assert. REGION_CLASSES / NodeView wrapper / toDOM 클래스
    // 사이의 mismatch (DOM_SELECTOR 가 더 이상 mount DOM 과 매칭 안 되는 회귀) 를 감지. 인스턴스
    // 마다 1회만 발화 — 첫 set() 후 PM transaction flush 까지 microtask wait.
    if (!__ENV_PRODUCTION__ && !this.domSelectorAsserted) {
      this.domSelectorAsserted = true
      queueMicrotask(() => {
        const mount = handle.getEditorDOM()
        const sel = DOM_SELECTOR[this.kind]
        if (mount != null && mount.querySelector(sel) == null) {
          console.warn(
            `[ngwEditor-0.2.11] ${this.kind} region not found in DOM (selector: ${sel}). ` +
              'DOM_SELECTOR / NodeView className / toDOM 클래스 정합 깨짐 가능성. ' +
              'REGION_CLASSES 일관성 재확인.',
          )
        }
      })
    }
  }

  /** 새 region 노드를 기존 노드 자리에 교체하거나 canonical 순서 기반 위치에 insert. */
  private replaceOrAppend(
    newNode: ProseMirrorNode,
    nodes: ReadonlyArray<{ pos: number; node: ProseMirrorNode }>,
  ): void {
    const core = this.adapter.lifecycle.getCore()
    if (core == null) return
    const { view } = core
    const { state } = view

    if (nodes.length > 0) {
      const first = nodes[0]
      const tr = state.tr.replaceWith(first.pos, first.pos + first.node.nodeSize, newNode)
      view.dispatch(tr)
      return
    }

    // 부재 — canonical 순서 기반 정확 위치에 insert.
    const insertPos = this.findInsertPos()
    const tr = state.tr.insert(insertPos, newNode)
    view.dispatch(tr)
  }

  /**
   * 본 kind 의 canonical index 보다 뒤에 있는 region 노드들 중 doc 에서 가장 앞쪽에
   * 위치하는 것의 pos 를 반환. 없으면 doc 끝.
   *
   * findNodes 반환은 PM 의 doc 순회 결과로 pos 가 노드 시작점 (= 해당 노드의
   * 직전 위치). insert 의 pos 인자에 그대로 전달하면 새 노드가 해당 노드 앞에 삽입된다.
   */
  private findInsertPos(): number {
    return this.findInsertPosFor(NODE_NAME[this.kind])
  }

  /**
   * 주어진 region node name 의 canonical index 보다 뒤에 있는 region 노드들 중 doc 에서 가장
   * 앞쪽에 위치하는 것의 pos 를 반환. 없으면 doc 끝. blank companion 삽입 위치 계산에도 사용.
   */
  private findInsertPosFor(nodeName: string): number {
    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return 0
    const { state } = core.view
    const idx = REGION_NODE_ORDER.indexOf(nodeName as (typeof REGION_NODE_ORDER)[number])
    for (let i = idx + 1; i < REGION_NODE_ORDER.length; i++) {
      const later = handle.findNodes(REGION_NODE_ORDER[i])
      if (later.length > 0) return later[0].pos
    }
    return state.doc.content.size
  }

  /**
   * companion blank region 자동 보장. EmailForm 등 호출부가 `insertMailLayout` 을 거치지 않고
   * 개별 `signature.set()` / `quotedHeader.set()` / `quotedBody.set()` 을 호출하므로, blank 영역
   * 생성을 set() 시점에 보장한다 (이미 있으면 no-op).
   *   - signature → blank_sign (nBody 와 signature 사이)
   *   - quotedHeader / quotedBody → blank_msg (signature/nBody 와 quoted 사이)
   */
  private ensureCompanionBlank(): void {
    let blankNodeName: 'blank_sign' | 'blank_msg' | null = null
    if (this.kind === 'signature') blankNodeName = 'blank_sign'
    else if (this.kind === 'quotedHeader' || this.kind === 'quotedBody') blankNodeName = 'blank_msg'
    if (blankNodeName == null) return

    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return
    // 이미 존재하면 중복 생성 안 함.
    if (handle.findNodes(blankNodeName).length > 0) return

    const { view } = core
    const { state } = view
    const nodeType = state.schema.nodes[blankNodeName]
    if (nodeType == null) return

    const parser = PMDOMParser.fromSchema(state.schema)
    const parsed = parser.parse(htmlToContainer('<p><br></p>'))
    // A-1.fix (ngwEditor-migration-0.2.12): PMDOMParser.parse 가 doc.content 시퀀스
    // 강제로 외부 paragraph 를 nbody 안에 자동 wrap. blank_sign / blank_msg 안에 nbody
    // 가 nested 되어 들어가는 회귀 차단. extensions/mailRegions.ts 의 unwrapAutoNbody
    // 주석 참조.
    const blankNode = nodeType.create({}, unwrapAutoNbody(parsed.content))
    const insertPos = this.findInsertPosFor(blankNodeName)
    view.dispatch(state.tr.insert(insertPos, blankNode))
  }

  get(useInnerHTML?: boolean): string {
    if (
      this.kind === 'quotedHeader' ||
      this.kind === 'quotedBody' ||
      this.kind === 'blankSign' ||
      this.kind === 'blankMsg'
    ) {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).get: not supported (see 03-interfaces.md §9)`,
      )
    }
    return this.serialize(useInnerHTML)
  }

  /** 내부용 — kind 분기 없이 항상 직렬화 (resetSnapshot 등 내부 호출 경로). */
  private serialize(useInnerHTML?: boolean): string {
    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return ''

    const nodes = handle.findNodes(NODE_NAME[this.kind])
    if (nodes.length === 0) {
      // 2026-05-19 보강 — nbody region 노드가 PM doc 에 없는 경우 doc 전체 직렬화로 폴백.
      // EmailForm/SignatureEdit 가 insertMailLayout 명령을 호출하지 않으면 nbody 노드는 없고
      // 본문은 PM doc 의 paragraph 노드들에 들어있다. 빈 문자열 반환 시 isChanged 가 본문
      // 변경을 감지 못 함. nbody region 만 폴백 (signature / quoted 는 의미상 region 부재 = 부재).
      if (this.kind === 'nBody') {
        return handle.getHTML({ includeStyles: true })
      }
      return ''
    }

    const ownerDoc = this.getOwnerDocument()
    if (ownerDoc == null) return ''

    const serializer = getDOMSerializer(core.view, core.view.state.schema)
    const target = nodes[0].node
    if (useInnerHTML === true) {
      const fragment = serializer.serializeFragment(target.content, { document: ownerDoc })
      const wrapper = ownerDoc.createElement('div')
      wrapper.appendChild(fragment)
      return wrapper.innerHTML
    }
    const dom = serializer.serializeNode(target, { document: ownerDoc })
    return dom.outerHTML
  }

  getElement(_ownerDoc?: Document): HTMLElement | null {
    if (this.kind === 'quotedHeader' || this.kind === 'quotedBody') {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).getElement: not supported (see 03-interfaces.md §9)`,
      )
    }
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return null
    const mount = handle.getEditorDOM()
    if (mount == null) return null
    // NgwEditor 는 inline 마운트라 ownerDoc 은 항상 mount 의 ownerDocument — 인자 무시.
    const found = mount.querySelector(DOM_SELECTOR[this.kind])
    return found instanceof HTMLElement ? found : null
  }

  has(): boolean {
    if (this.kind === 'nBody' || this.kind === 'quotedHeader') {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).has: not supported (see 03-interfaces.md §9)`,
      )
    }
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return false
    return handle.findNodes(NODE_NAME[this.kind]).length > 0
  }

  remove(): void {
    if (this.kind === 'nBody' || this.kind === 'quotedHeader') {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).remove: not supported (see 03-interfaces.md §9)`,
      )
    }
    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return

    const nodes = handle.findNodes(NODE_NAME[this.kind])
    if (nodes.length === 0) return

    const { view } = core
    let { tr } = view.state

    // signature 제거 시 짝이 되는 blank_sign 도 함께 제거 (orphan 빈 줄 방지). 단 사용자가
    // blank_sign 에 입력한 실제 텍스트가 있으면 nBody 끝으로 이동해 보존한다. 단일 transaction
    // (1회 dispatch) 으로 처리해 undo 가 signature/blank_sign 을 한 번에 복원하도록 한다.
    if (this.kind === 'signature') {
      const blankSignNodes = handle.findNodes('blank_sign')
      const nbodyNodes = handle.findNodes('nbody')
      const nbodyEnd =
        nbodyNodes.length > 0 ? nbodyNodes[0].pos + nbodyNodes[0].node.nodeSize - 1 : null
      // 실제 텍스트가 있는 blank_sign 만 자식 노드 보존 (빈 줄 <p><br></p> 는 스킵 → nBody 누적 방지).
      const movedNodes: ProseMirrorNode[] = []
      for (const n of blankSignNodes) {
        if (n.node.textContent.trim().length === 0) continue
        const frag = n.node.content
        for (let i = 0; i < frag.childCount; i++) movedNodes.push(frag.child(i))
      }

      // 삭제: signature + blank_sign 을 doc 뒤쪽(pos 큰 것)부터 — 앞쪽 pos 무효화 방지.
      const toDelete = [...nodes, ...blankSignNodes].sort((a, b) => b.pos - a.pos)
      for (const t of toDelete) {
        tr = tr.delete(t.pos, t.pos + t.node.nodeSize)
      }
      // 보존할 내용을 nBody 끝으로 이동. nBody 는 signature/blank_sign 보다 앞이라 위 삭제가
      // nbodyEnd 위치를 밀지 않으므로 원본 nbodyEnd 를 그대로 사용 가능 (mapping 불필요).
      if (nbodyEnd != null && movedNodes.length > 0) {
        tr = tr.insert(nbodyEnd, movedNodes)
      }
      view.dispatch(tr)
      return
    }

    // 뒤에서부터 제거 — pos 가 앞에서부터 영향받지 않도록.
    for (let i = nodes.length - 1; i >= 0; i--) {
      const target = nodes[i]
      tr = tr.delete(target.pos, target.pos + target.node.nodeSize)
    }
    view.dispatch(tr)
  }

  isChanged(): boolean {
    if (this.kind === 'signature' || this.kind === 'quotedHeader') {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).isChanged: not supported (see 03-interfaces.md §9)`,
      )
    }
    // B (ngwEditor-migration-0.2.12): PM Node.eq 구조적 비교 (QA Q1-3 옵션 A).
    // HTML 정규화 (td>p unwrap, data-colwidth 변환, 빈 블록 br 보강 등) 와 무관한 false
    // positive 차단. baseline 은 resetSnapshot 시점에 캡쳐된 region 노드 또는 nbody 부재 시 doc.
    //
    // EmailForm.isModified() → callSave() 자동 저장 종단 경로:
    //   1. resetChangedState 직후: initialRegionNode 가 현재 doc/node 와 동일 → eq() true → false
    //   2. 사용자 편집 후: doc 변경으로 새 Node 인스턴스 → eq() false → true → callSave 발동
    //   3. quotedBody collapsed 토글: attrs 변경으로 Node.eq false → 기존 string 비교(toDOM 의
    //      'fold' class 변경 반영) 와 parity. NgwEditorState.ts:37-40 의 callSave silent skip
    //      가드 정합 유지.
    //
    // initialRegionNode == null: baseline 미설정 (resetChangedState 미호출 또는 nbody 가
    // 어디에도 없는 비정상) → false 반환 (호출자 책임).
    if (this.initialRegionNode == null) return false
    try {
      const current = this.captureRegionNode()
      if (current == null) return false
      return !current.eq(this.initialRegionNode)
    } catch {
      return false
    }
  }

  setEditable(editable: boolean): void {
    if (this.kind === 'signature' || this.kind === 'quotedHeader') {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).setEditable: not supported (see 03-interfaces.md §9)`,
      )
    }
    this.editable = editable
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return
    const mount = handle.getEditorDOM()
    if (mount == null) return
    const elems = mount.querySelectorAll(DOM_SELECTOR[this.kind])
    elems.forEach((elem) => {
      if (elem instanceof HTMLElement) {
        elem.setAttribute('contenteditable', String(editable))
      }
    })
  }

  isEditable(): boolean {
    if (this.kind !== 'nBody') {
      throw new Error(
        `NgwEditorMailRegion(${this.kind}).isEditable: not supported (see 03-interfaces.md §9)`,
      )
    }
    return this.editable
  }

  /** DOM serializer 가 사용할 ownerDocument 결정. host 가 init 됐을 때만 사용 가능. */
  private getOwnerDocument(): Document | null {
    const host = this.adapter.getHostInternal()
    if (host == null) return null
    try {
      return host.getDocument()
    } catch {
      return null
    }
  }
}
